import { useCallback, useEffect, useState, type ReactNode } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { AlertTriangle, CheckCircle2 } from 'lucide-react';
import type { BillingCheckoutResponse, ServerBillingStatus } from '@oracord/shared';
import { PageHeader, SectionCard, StatusBadge } from '../components/WebPresentation';
import { apiFetch, ApiError } from '../lib/api';
import { billingPlanCards, type BillingPlanDisplay, type BillingPlanMapping } from '../lib/billing-pricing';
import { checkoutReturnStatus, shouldPollBillingCheckoutReturn, shouldShowCheckoutSyncNotice } from '../lib/billing-checkout-return';
import { useSessionStore } from '../stores/session';

// Billing checkout surface.
// The page intentionally keeps the customer-facing view simple: one
// container with the three configured Oracord pricing options. Stripe
// remains the checkout/payment source of truth behind each card.

export default function Billing() {
  const navigate = useNavigate();
  const location = useLocation();
  const activeOrgId = useSessionStore(s => s.activeOrganizationId);

  const [plans, setPlans] = useState<BillingPlanMapping[]>([]);
  const [billingStatus, setBillingStatus] = useState<ServerBillingStatus | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [acting, setActing] = useState<string | null>(null);

  const checkoutReturn = checkoutReturnStatus({
    pathname: location.pathname,
    search: location.search,
    billingActive: Boolean(billingStatus?.active),
  });
  const showCheckoutSyncNotice = shouldShowCheckoutSyncNotice(checkoutReturn);

  const load = useCallback(async () => {
    if (!activeOrgId) return;
    setLoading(true);
    setError(null);
    try {
      const [p, status] = await Promise.all([
        apiFetch<{ plans: BillingPlanMapping[] }>('/api/billing/plans'),
        apiFetch<ServerBillingStatus>('/api/billing/status'),
      ]);
      setPlans(p.plans);
      setBillingStatus(status);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : (err as Error).message || 'Failed to load');
    } finally {
      setLoading(false);
    }
  }, [activeOrgId]);

  useEffect(() => {
    void load();
  }, [load]);

  useEffect(() => {
    if (checkoutReturn === 'redirect-setup') {
      navigate('/setup?billing=success', { replace: true });
    }
  }, [checkoutReturn, navigate]);

  useEffect(() => {
    if (!activeOrgId || !shouldPollBillingCheckoutReturn(checkoutReturn)) return;
    let attempts = 0;
    const interval = window.setInterval(() => {
      attempts += 1;
      if (attempts > 12) {
        window.clearInterval(interval);
        return;
      }
      void load();
    }, 2500);
    return () => window.clearInterval(interval);
  }, [activeOrgId, checkoutReturn, load]);

  async function startCheckout(priceId: string) {
    setActing(`checkout:${priceId}`);
    setError(null);
    try {
      const res = await apiFetch<BillingCheckoutResponse>('/api/billing/checkout', {
        method: 'POST',
        body: { priceId },
      });
      window.location.href = res.url;
    } catch (err) {
      setError(err instanceof ApiError ? err.message : (err as Error).message);
      setActing(null);
    }
  }

  if (!activeOrgId) return <BillingNoOrgState />;
  if (loading && plans.length === 0) return <BillingLoadingState />;

  return (
    <div className="max-w-6xl space-y-5">
      <PageHeader
        eyebrow="Billing"
        title="Choose your plan"
        description="Pick Solo or Team depending on how you plan to use Oracord. Add extra business lines later if your team grows beyond the included lines."
      />

      {showCheckoutSyncNotice && (
        <NoticeCard tone="emerald" icon={<CheckCircle2 className="h-5 w-5 text-emerald-600" />}>
          Checkout complete — it can take a moment for billing to sync. Refresh if the plan hasn't updated yet.
        </NoticeCard>
      )}

      {error && (
        <NoticeCard tone="rose" icon={<AlertTriangle className="h-5 w-5 text-red-500" />}>
          {error}
        </NoticeCard>
      )}

      <PricingPanel
        plans={plans}
        billingStatus={billingStatus}
        acting={acting}
        startCheckout={startCheckout}
      />
    </div>
  );
}

function BillingLoadingState() {
  return (
    <div className="max-w-6xl space-y-5">
      <PageHeader
        eyebrow="Billing"
        title="Loading plans…"
        description="Fetching the configured checkout options."
      />
      <SectionCard className="px-5 py-12 text-center">
        <div className="mx-auto h-10 w-10 animate-pulse rounded-2xl bg-blue-50" />
        <p className="mt-3 text-sm font-medium text-slate-900">Loading pricing</p>
        <p className="mt-1 text-xs text-slate-500">Solo, Team, and extra business line options will appear here.</p>
      </SectionCard>
    </div>
  );
}

function BillingNoOrgState() {
  return (
    <div className="max-w-6xl space-y-5">
      <PageHeader
        eyebrow="Billing"
        title="Billing needs an organization"
        description="Select an organization from the app shell before loading checkout options."
        badge={<StatusBadge tone="amber">No organization selected</StatusBadge>}
      />
      <SectionCard className="border-amber-200 bg-amber-50 px-5 py-4 text-sm text-amber-800">
        No organization selected.
      </SectionCard>
    </div>
  );
}

function PricingPanel({
  plans,
  billingStatus,
  acting,
  startCheckout,
}: {
  plans: BillingPlanMapping[];
  billingStatus: ServerBillingStatus | null;
  acting: string | null;
  startCheckout: (priceId: string) => void | Promise<void>;
}) {
  const cards = billingPlanCards(plans);
  return (
    <SectionCard className="p-5 sm:p-6">
      <div className="flex flex-col gap-3 lg:flex-row lg:items-start lg:justify-between">
        <div>
          <div className="text-xs font-semibold uppercase tracking-[0.16em] text-slate-500">Pricing</div>
          <h2 className="text-xl font-semibold text-slate-950">Solo and Team are the main subscriptions</h2>
          <p className="mt-1 max-w-3xl text-sm leading-6 text-slate-600">
            Extra business lines are available for additional team members beyond the lines included with Team.
          </p>
        </div>
      </div>

      <div className="mt-6 grid gap-4 lg:grid-cols-3">
        {cards.map(card => (
          <PricingCheckoutCard
            key={card.priceId}
            card={card}
            billingStatus={billingStatus}
            acting={acting}
            startCheckout={startCheckout}
          />
        ))}
      </div>

      {plans.length === 0 && (
        <p className="mt-5 rounded-2xl border border-dashed border-slate-200 bg-slate-50/80 px-4 py-3 text-xs text-slate-500">
          No paid plans are configured. Set <code>STRIPE_PRICE_ALLOWLIST</code> on the server to enable checkout.
        </p>
      )}
    </SectionCard>
  );
}

function PricingCheckoutCard({
  card,
  billingStatus,
  acting,
  startCheckout,
}: {
  card: BillingPlanDisplay;
  billingStatus: ServerBillingStatus | null;
  acting: string | null;
  startCheckout: (priceId: string) => void | Promise<void>;
}) {
  const isOpening = acting === `checkout:${card.priceId}`;
  const isActivePlan = Boolean(billingStatus?.active && billingStatus.plan === card.plan);
  const emphasis = isActivePlan
    ? 'border-emerald-200 bg-emerald-50/50'
    : card.plan === 'team'
      ? 'border-blue-200 bg-blue-50/40'
      : card.kind === 'add_on'
        ? 'border-amber-200 bg-amber-50/40'
        : 'border-slate-200 bg-white';

  return (
    <button
      type="button"
      onClick={() => startCheckout(card.priceId)}
      disabled={acting !== null || isActivePlan}
      className={`flex h-full flex-col rounded-3xl border px-5 py-5 text-left text-sm shadow-sm shadow-slate-200/60 transition hover:-translate-y-0.5 hover:border-blue-300 hover:shadow-md disabled:translate-y-0 disabled:opacity-60 ${emphasis}`}
    >
      <span className="inline-flex w-fit rounded-full bg-white/80 px-2.5 py-1 text-[10px] font-semibold uppercase tracking-[0.14em] text-slate-500 shadow-sm shadow-slate-200/60">
        {isActivePlan ? 'Current active plan' : card.badge}
      </span>
      <span className="mt-4 block text-xl font-semibold text-slate-950">{card.title}</span>
      <span className="mt-1 block text-3xl font-bold text-slate-950">{card.price}</span>
      <span className="text-xs font-medium text-slate-500">{card.cadence}</span>
      <span className="mt-4 block leading-6 text-slate-600">{card.description}</span>
      <span className="mt-4 space-y-1.5 text-xs text-slate-600">
        {card.highlights.map(item => (
          <span key={item} className="block">• {item}</span>
        ))}
      </span>
      <span className="mt-auto pt-5">
        <span className="inline-flex w-full items-center justify-center rounded-full bg-slate-950 px-3 py-2.5 text-sm font-semibold text-white shadow-sm shadow-slate-300">
          {isActivePlan ? 'Active plan' : isOpening ? 'Opening…' : card.cta}
        </span>
      </span>
    </button>
  );
}

function NoticeCard({ tone, icon, children }: { tone: 'emerald' | 'rose'; icon: ReactNode; children: ReactNode }) {
  const toneClass = tone === 'emerald'
    ? 'border-emerald-200 bg-emerald-50 text-emerald-800'
    : 'border-red-200 bg-red-50 text-red-800';
  return (
    <SectionCard className={`${toneClass} px-4 py-3 text-sm`}>
      <div className="flex items-start gap-2">
        {icon}
        <div>{children}</div>
      </div>
    </SectionCard>
  );
}
